/**
 * PsicoSalud — Orquestación de la sesión remota (Módulo 3).
 *
 * Vincula cita, sala de videollamada e historial clínico en una sola
 * pantalla: el profesional atiende y toma notas sin cambiar de contexto,
 * que es el punto que la ficha del proyecto identifica como ausente en
 * las herramientas actuales de telepsicología.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import JitsiRoom from '@/components/JitsiRoom';
import { appointmentsApi, clinicalRecordsApi, videoApi } from '@/api';
import { ApiError } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import type { Appointment, ClinicalRecord, JoinSession, VideoSession } from '@/types';

export default function VideoSessionPage() {
  const { t } = useTranslation();
  const { appointmentId = '' } = useParams();
  const { user } = useAuth();

  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [session, setSession] = useState<VideoSession | null>(null);
  const [joined, setJoined] = useState<JoinSession | null>(null);
  const [loading, setLoading] = useState(true);
  // Panel lateral del historial. Antes había un enlace que navegaba fuera de
  // la pantalla y, al hacerlo, desmontaba la sala de Jitsi: el profesional
  // perdía la videollamada por consultar una nota (tarea 17 del instrumento).
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [historial, setHistorial] = useState<ClinicalRecord[] | null>(null);
  const [nota, setNota] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(
    null,
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await appointmentsApi.get(appointmentId);
      setAppointment(detail);
      try {
        setSession(await videoApi.getByAppointment(appointmentId));
      } catch (error) {
        // Una cita presencial promovida a remota puede no tener sala
        // todavía: no es un error, se ofrece crearla.
        if (!(error instanceof ApiError && error.status === 404)) throw error;
        setSession(null);
      }
    } catch {
      setFeedback({ type: 'error', text: t('errors.network') });
    } finally {
      setLoading(false);
    }
  }, [appointmentId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    try {
      setSession(await videoApi.create(appointmentId));
    } catch (error) {
      setFeedback({
        type: 'error',
        text: error instanceof ApiError ? error.message : t('errors.generic'),
      });
    }
  };

  const handleJoin = async () => {
    if (!session) return;
    try {
      setJoined(await videoApi.join(session.id));
    } catch (error) {
      setFeedback({
        type: 'error',
        text: error instanceof ApiError ? error.message : t('errors.generic'),
      });
    }
  };

  const handleEnd = useCallback(async () => {
    if (!session) return;
    if (!window.confirm(t('video.endConfirm'))) return;
    try {
      setSession(await videoApi.end(session.id));
      setJoined(null);
      setFeedback({ type: 'info', text: t('video.ended') });
    } catch {
      setFeedback({ type: 'error', text: t('errors.generic') });
    }
  }, [session, t]);

  /**
   * Abre o cierra el panel, cargando el historial la primera vez.
   *
   * La carga es perezosa a propósito: quien nunca abre el panel no paga la
   * consulta, y el contenido clínico solo se descifra si alguien va a leerlo.
   */
  const alternarPanel = async () => {
    const abriendo = !panelAbierto;
    setPanelAbierto(abriendo);
    if (abriendo && historial === null && appointment) {
      try {
        setHistorial(await clinicalRecordsApi.patientHistory(appointment.patient_id));
      } catch {
        setHistorial([]);
        setFeedback({ type: 'error', text: t('errors.network') });
      }
    }
  };

  /** Agrega una nota al registro más reciente, sin salir de la sesión. */
  const guardarNota = async () => {
    const registro = historial?.[0];
    if (!registro || !nota.trim()) return;
    setGuardandoNota(true);
    try {
      await clinicalRecordsApi.addNote(registro.id, { content: nota.trim() });
      setNota('');
      if (appointment) {
        setHistorial(await clinicalRecordsApi.patientHistory(appointment.patient_id));
      }
      setFeedback({ type: 'success', text: t('record.noteAdded') });
    } catch {
      setFeedback({ type: 'error', text: t('errors.generic') });
    } finally {
      setGuardandoNota(false);
    }
  };

  const copyLink = async () => {
    if (!session) return;
    // `navigator.clipboard` solo existe en contextos seguros (HTTPS o
    // localhost). Mientras el despliegue siga siendo HTTP por IP —lo que
    // ocurre hasta que `psicosalud.com.py` delegue— la propiedad es
    // `undefined` y una llamada directa revienta con un TypeError que el
    // `void` se come: el botón no haría nada, sin ningún aviso. Por eso se
    // comprueba y se cae a un plan B visible.
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(session.meeting_url);
        setFeedback({ type: 'success', text: t('video.linkCopied') });
        return;
      } catch {
        // Sigue el plan B: algunos navegadores la rechazan por permisos.
      }
    }
    window.prompt(t('video.copyLink'), session.meeting_url);
  };

  // Qué protección aplica realmente la sala. Antes de unirse lo sabemos por
  // la sesión; después, por la respuesta del ingreso. El texto se ajusta a
  // eso en vez de prometer siempre un token: con el despliegue público de
  // Jitsi no lo hay, y decirlo en falso sería mentirle al profesional sobre
  // la confidencialidad de la consulta.
  const tokenActivo = joined?.token_aplicado ?? session?.token_disponible ?? false;

  if (loading) return <div className="empty-state">{t('common.loading')}</div>;

  return (
    <>
      <header className="page-header with-flex">
        <div>
          <span className="page-eyebrow">{t('video.title')}</span>
          <h1>{t('video.title')}</h1>
          {appointment && (
            <p className="page-descripcion">
              {appointment.patient_name ? (
                <Link
                  to={`/pacientes/${appointment.patient_id}`}
                  style={{ color: 'var(--principal)', fontWeight: 'var(--peso-medio)' }}
                >
                  {appointment.patient_name}
                </Link>
              ) : (
                <span>—</span>
              )}
              <span className="t-numerico">
                {' · '}
                {new Date(appointment.start_time).toLocaleString('es-PY')}
              </span>
            </p>
          )}
        </div>
        {appointment && (
          <div className="page-acciones">
            <button
              className={panelAbierto ? 'is-pequeno' : 'secondary is-pequeno'}
              onClick={() => void alternarPanel()}
              data-cy="video-toggle-record"
            >
              {panelAbierto ? t('video.hideRecord') : t('video.showRecord')}
            </button>
          </div>
        )}
      </header>

      {feedback && (
        <div className={`notice notice-${feedback.type}`} role="status" data-cy="video-feedback">
          {feedback.text}
        </div>
      )}

      {!session ? (
        <div className="card">
          <div className="card-header">
            <div>
              <span className="card-eyebrow">{t('video.title')}</span>
              <h2>{t('video.noSession')}</h2>
            </div>
          </div>
          <p className="t-pequeno t-suave" style={{ marginBottom: 'var(--espacio-4)' }}>
            {t('video.privacyNoToken')}
          </p>
          <button onClick={() => void handleCreate()} data-cy="video-create">
            {t('video.createSession')}
          </button>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div>
              <span className="card-eyebrow">{t('video.title')}</span>
              <h2>
                {t('agenda.status')}:{' '}
                <span className={`badge badge-${session.status === 'ENDED' ? 'neutral' : 'info'}`} data-cy="video-status">
                  {session.status}
                </span>
              </h2>
            </div>
            <div className="button-row">
              <button
                className="secondary is-pequeno"
                onClick={() => void copyLink()}
                data-cy="video-copy-link"
              >
                {t('video.copyLink')}
              </button>
              {!joined && session.status !== 'ENDED' && (
                <button onClick={() => void handleJoin()} data-cy="video-join">
                  {t('video.join')}
                </button>
              )}
              {session.status !== 'ENDED' && (
                <button
                  className="danger is-pequeno"
                  onClick={() => void handleEnd()}
                  data-cy="video-end"
                >
                  {t('video.end')}
                </button>
              )}
            </div>
          </div>

          <p className="t-pequeno t-suave" style={{ marginBottom: 'var(--espacio-4)' }}>
            {t(tokenActivo ? 'video.privacy' : 'video.privacyNoToken')}
          </p>

          {/* El contenedor está siempre presente, con panel o sin él. Si la
              estructura del árbol cambiara al abrir el panel, React
              desmontaría y volvería a montar `JitsiRoom`, y la videollamada
              se cortaría — que es exactamente el problema que este panel
              viene a resolver.

              `onLeave` se pasa por referencia y no como flecha: `JitsiRoom`
              lo tiene en las dependencias de su efecto, y el cleanup de ese
              efecto llama `dispose()`. Una flecha inline es una función nueva
              en cada render, de modo que escribir una letra en la nota de la
              sesión —que dispara un re-render— remonta la sala entera y corta
              la llamada. `handleEnd` ya es un `useCallback`, así que pasarlo
              directo mantiene la identidad estable. */}
          <div className={`sesion-layout${panelAbierto ? ' con-panel' : ''}`}>
            <div>
              {joined && (
                <JitsiRoom
                  session={joined}
                  displayName={user?.full_name ?? 'Profesional'}
                  onLeave={handleEnd}
                />
              )}
            </div>

            {panelAbierto && (
              <aside className="sesion-panel" data-cy="video-record-panel">
                <h3>{t('record.title')}</h3>

                {historial === null ? (
                  <p className="t-pequeno t-suave">{t('common.loading')}</p>
                ) : historial.length === 0 ? (
                  <p className="empty-state">{t('record.empty')}</p>
                ) : (
                  <>
                    <div className="field">
                      <label htmlFor="nota-en-sesion">{t('record.addNote')}</label>
                      <textarea
                        id="nota-en-sesion"
                        rows={3}
                        value={nota}
                        onChange={(event) => setNota(event.target.value)}
                        placeholder={t('video.noteWhileInSession')}
                        data-cy="video-note-content"
                      />
                      <button
                        onClick={() => void guardarNota()}
                        disabled={guardandoNota || !nota.trim()}
                        data-cy="video-note-save"
                        style={{ marginTop: 'var(--espacio-2)' }}
                      >
                        {guardandoNota ? t('common.loading') : t('record.addNote')}
                      </button>
                    </div>

                    {historial.map((registro) => (
                      <div className="registro" key={registro.id} data-cy="video-record-entry">
                        <p className="t-pequeno" style={{ margin: '0 0 0.3rem' }}>
                          <strong>
                            {new Date(registro.session_date).toLocaleDateString('es-PY')}
                          </strong>{' '}
                          · {t(`record.types.${registro.record_type}`)}
                        </p>
                        {registro.diagnosis && (
                          <p className="t-pequeno" style={{ margin: '0 0 0.3rem' }}>
                            <strong>{t('record.diagnosis')}:</strong> {registro.diagnosis}
                          </p>
                        )}
                        {registro.notes.map((n) => (
                          <p className="t-pequeno t-suave" key={n.id} style={{ margin: '0 0 0.3rem' }}>
                            {n.content}
                          </p>
                        ))}
                      </div>
                    ))}
                  </>
                )}
              </aside>
            )}
          </div>

          {session.status === 'ENDED' && session.duration_seconds !== null && (
            <p style={{ marginTop: 'var(--espacio-4)' }}>
              <strong>{t('video.duration')}:</strong>{' '}
              <span className="t-numerico">
                {Math.round(session.duration_seconds / 60)} min
              </span>
            </p>
          )}
        </div>
      )}
    </>
  );
}
